import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { AlertTriangle, ShieldAlert, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Reusable Hard Delete Dialog.
 *
 * Security flow (enforced by both backend + UI):
 *   1. Show warning + impact preview (counts of cascading rows)
 *   2. User must type the literal string "DELETE"
 *   3. User must enter their account password
 *   4. Confirm button is disabled until BOTH conditions pass
 *   5. Backend re-verifies the password via bcrypt before deleting
 *
 * Props
 *  open, onOpenChange         dialog state
 *  title                      "Customer", "Loan", "Shop", etc.
 *  targetLabel                user-facing name (e.g. "Ali Khan")
 *  endpoint                   relative API path, e.g. "/customers/abc123"
 *  previewEndpoint            optional GET endpoint for impact + blockers
 *  onDeleted(result)          callback after successful 200
 *  description                optional extra context line
 *  extraImpact                array of {label, value} rendered after preview
 */
export default function HardDeleteDialog({
  open, onOpenChange, title = "Item", targetLabel = "",
  endpoint, previewEndpoint, onDeleted, description, extraImpact,
}) {
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setConfirmText(""); setPassword(""); setPreview(null);
    if (!previewEndpoint) return;
    setPreviewLoading(true);
    api.get(previewEndpoint)
      .then((r) => setPreview(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setPreviewLoading(false));
  }, [open, previewEndpoint]);

  const blockers = preview?.blockers || [];
  const canDelete = (preview?.can_delete !== false) && blockers.length === 0;
  const ready = canDelete && confirmText.trim() === "DELETE" && password.length > 0;

  const submit = async () => {
    if (!ready) return;
    setSubmitting(true);
    try {
      const res = await api.delete(endpoint, {
        data: { confirm: "DELETE", password },
      });
      toast.success(`${title} permanently deleted.`);
      onOpenChange(false);
      onDeleted?.(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const previewRows = (() => {
    if (!preview) return [];
    const out = [];
    // Whitelisted keys we render in friendly labels
    const map = {
      customers_count: "Customers",
      loans_count: "Loans",
      payments_count: "Payments",
      users_count: "User accounts",
      active_loans: "Active loans (block!)",
      loans_total: "Total loans",
      payments_total: "Payments",
      followups: "Follow-up notes",
      documents: "Uploaded files",
      shops_total: "Onboarded shops",
      shops_active: "Active shops",
      commissions_count: "Commission records",
    };
    for (const [k, label] of Object.entries(map)) {
      if (preview[k] !== undefined) out.push({ label, value: preview[k] });
    }
    return out;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto bg-white text-slate-900 border-2 border-red-200"
        data-testid="hard-delete-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            Delete {title} permanently?
          </DialogTitle>
          <DialogDescription className="sr-only">
            This action permanently deletes the selected {title.toLowerCase()} and cannot be undone. Type DELETE and re-enter your account password to confirm.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-900">
            <div className="font-semibold flex items-center gap-1.5">
              <ShieldAlert className="w-4 h-4" /> This action is permanent and cannot be undone.
            </div>
            {targetLabel && (
              <div className="mt-1 text-red-800/90">
                Target: <span className="font-bold">{targetLabel}</span>
              </div>
            )}
            {description && (
              <div className="mt-1 text-red-700/80 text-xs">{description}</div>
            )}
          </div>

          {previewLoading && (
            <div className="text-xs text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading impact…</div>
          )}

          {(previewRows.length > 0 || (extraImpact || []).length > 0) && (
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 space-y-1.5 text-xs" data-testid="hard-delete-impact">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Cascade impact</div>
              {previewRows.map((r) => (
                <div key={r.label} className="flex justify-between">
                  <span className="text-slate-600">{r.label}</span>
                  <span className="tabular font-bold text-slate-900">{r.value}</span>
                </div>
              ))}
              {(extraImpact || []).map((r) => (
                <div key={r.label} className="flex justify-between">
                  <span className="text-slate-600">{r.label}</span>
                  <span className="tabular font-bold text-slate-900">{r.value}</span>
                </div>
              ))}
              {preview?.preserved && Object.values(preview.preserved).some((v) => v > 0) && (
                <div className="mt-2 pt-2 border-t border-slate-200 text-[10px] text-emerald-700">
                  <span className="font-semibold">PRESERVED (audit):</span>{" "}
                  {Object.entries(preview.preserved)
                    .filter(([, v]) => v > 0)
                    .map(([k, v]) => `${k.replace(/_/g, " ")} (${v})`)
                    .join(" · ")}
                </div>
              )}
            </div>
          )}

          {blockers.length > 0 && (
            <div className="rounded-md bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900" data-testid="hard-delete-blockers">
              <div className="font-bold mb-1">Cannot delete:</div>
              <ul className="list-disc pl-4 space-y-0.5">
                {blockers.map((b) => <li key={b}>{b}</li>)}
              </ul>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">
              Type <span className="font-mono bg-red-100 px-1.5 py-0.5 rounded text-red-700">DELETE</span> to confirm
            </Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              disabled={!canDelete || submitting}
              autoFocus
              data-testid="hard-delete-confirm-input"
              className={confirmText && confirmText !== "DELETE" ? "border-red-300" : ""}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">
              Re-enter your account password
            </Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Account password"
              autoComplete="current-password"
              disabled={!canDelete || submitting}
              data-testid="hard-delete-password-input"
            />
            <div className="text-[11px] text-slate-500">
              For security, your password is verified before permanent deletion.
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="hard-delete-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={!ready || submitting}
            className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
            data-testid="hard-delete-confirm-btn"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Deleting…</>) : "Delete Permanently"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
